import { EditorState, RawDraftContentState } from 'draft-js';
import { editorStateFromRaw } from 'megadraft';
import { isString } from 'lodash';

export interface CountType {
  wordCount: number;
  charCount: number;
}

const getCountFromText = (text: string): CountType => {
  if (!text) {
    return { wordCount: 0, charCount: 0 };
  }
  const charCount = Array.from(text).length;
  const words = text.split(' ').filter((word: string) => word.length > 0);
  const wordCount =
    words.length === 1 ? (words[0].length === 0 ? 0 : 1) : words.length;

  return { charCount, wordCount };
};

const getCountFromEditorState = (e: EditorState): CountType => {
  const regex = /(?:\r\n|\r|\n)/g; // new line, carriage return, line feed
  const contentState = e.getCurrentContent();
  const plainText = contentState.getPlainText('');
  const text = plainText.replace(regex, '').trim(); // replace above characters w/ nothing
  const charCount = Array.from(text).length;
  const words = text.split(' ').filter((word: string) => word.length > 0);
  const wordCount =
    words.length === 1 ? (words[0].length === 0 ? 0 : 1) : words.length;

  const nestedBlockTypes = ['aside', 'quote'] as const;

  const blocksCount = contentState
    .getBlocksAsArray()
    .filter(
      (block) =>
        block.get('type') === 'atomic' &&
        nestedBlockTypes.includes(block.getIn(['data', 'type'])),
    )
    .reduce(
      (count, block) => {
        const blockDataType = block.getIn(['data', 'type']);
        const nestedDraftContent = block.getIn([
          'data',
          blockDataType === 'quote' ? 'quoteDraft' : 'frameDraft',
        ]);
        const nestedTextField = block.getIn([
          'data',
          blockDataType === 'quote' ? 'quoteAuthor' : 'frameTitle',
        ]);
        const nestedTextCount =
          blockDataType === 'quote' &&
          nestedTextField &&
          !isString(nestedTextField)
            ? getCountFromEditorState(
                editorStateFromRaw(
                  nestedTextField.toJS
                    ? nestedTextField.toJS()
                    : nestedTextField,
                ),
              )
            : getCountFromText(nestedTextField);
        let nestedDraftCount = { wordCount: 0, charCount: 0 };

        if (!nestedDraftContent && !nestedTextField) {
          return count;
        }

        if (nestedDraftContent) {
          let nestedDraftContentJSON = nestedDraftContent;

          if (isString(nestedDraftContentJSON)) {
            try {
              nestedDraftContentJSON = JSON.parse(nestedDraftContent);
            } catch {}
          }

          nestedDraftCount = getCountFromEditorState(
            editorStateFromRaw(
              nestedDraftContentJSON.toJS
                ? nestedDraftContentJSON.toJS()
                : nestedDraftContentJSON,
            ),
          );
        }

        return {
          wordCount:
            count.wordCount +
            nestedDraftCount.wordCount +
            nestedTextCount.wordCount,
          charCount:
            count.charCount +
            nestedDraftCount.charCount +
            nestedTextCount.charCount,
        };
      },
      { wordCount: 0, charCount: 0 },
    );

  return {
    charCount: charCount + blocksCount.charCount,
    wordCount: wordCount + blocksCount.wordCount,
  };
};

export function getCount(
  content: string | null | RawDraftContentState,
  type?: 'Draft',
) {
  return content
    ? type
      ? getCountFromEditorState(editorStateFromRaw(content))
      : getCountFromText(content as string)
    : { wordCount: 0, charCount: 0 };
}
